import { useEffect, useRef } from 'react';
import { useLocation, type Location } from 'react-router-dom';
import { t } from '@/i18n';
import { savePostLoginRedirect } from '@/utils/postLoginRedirect';
import styles from './GoogleSignInButton.module.css';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            ux_mode: 'redirect';
            login_uri: string;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              type?: 'standard' | 'icon';
              width?: number;
            },
          ) => void;
        };
      };
    };
  }
}

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client?hl=ru';
const GIS_SCRIPT_ID = 'gsi-client';
const GOOGLE_REDIRECT_PATH = '/api/v1/auth/google/redirect';

type LocationState = { from?: string } | null;

type GoogleSignInButtonProps = {
  mode?: 'login' | 'register';
};

function GoogleIcon() {
  return (
    <svg
      className={styles.icon}
      width="18"
      height="18"
      viewBox="0 0 18 18"
      aria-hidden
    >
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.616z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.441 1.235l2.581-2.581C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

export function GoogleSignInButton({
  mode = 'login',
}: GoogleSignInButtonProps) {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  const wrapRef = useRef<HTMLDivElement>(null);
  const realRef = useRef<HTMLDivElement>(null);

  const location = useLocation() as Location & { state: LocationState };
  const from = location.state?.from;

  useEffect(() => {
    if (!clientId) return;

    const apiOrigin =
      (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
      window.location.origin;

    // Redirect mode navigates this very tab through Google and back, so the
    // page (and `location.state`) is gone by the time the user returns.
    savePostLoginRedirect(from);

    const init = () => {
      const wrap = wrapRef.current;
      const real = realRef.current;
      if (!window.google || !wrap || !real) return;

      window.google.accounts.id.initialize({
        client_id: clientId,
        ux_mode: 'redirect',
        login_uri: `${apiOrigin}${GOOGLE_REDIRECT_PATH}`,
      });

      real.innerHTML = '';
      window.google.accounts.id.renderButton(real, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        width: wrap.offsetWidth || 360,
      });
    };

    if (window.google) {
      init();
      return;
    }

    if (!document.getElementById(GIS_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = GIS_SCRIPT_ID;
      script.src = GIS_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = init;
      document.head.appendChild(script);
    } else {
      const existing = document.getElementById(
        GIS_SCRIPT_ID,
      ) as HTMLScriptElement;
      const prev = existing.onload;
      existing.onload = (e) => {
        if (typeof prev === 'function') prev.call(existing, e);
        init();
      };
    }
  }, [clientId, from]);

  if (!clientId) return null;

  const label =
    mode === 'register' ? t.auth.googleRegister : t.auth.googleLogin;

  return (
    <div ref={wrapRef} className={styles.wrap}>
      {/* Purely decorative — Google renders its real button (a cross-origin
          iframe) into .real, stretched on top and made invisible. A
          script-dispatched click can't reach into that iframe, so this
          layer can't be a functional proxy button; it only supplies the
          look. Real clicks land on the invisible iframe above it. */}
      <div className={styles.decorative} aria-hidden="true">
        <GoogleIcon />
        <span>{label}</span>
      </div>
      <div ref={realRef} className={styles.real} aria-label={label} />
    </div>
  );
}
